import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CreditCard,
  Eye,
  EyeOff,
  Globe,
  Loader2,
  Lock,
  Mail,
  Package2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/app/contexts/AuthContext";
import { ROUTES } from "@/app/config/routes";
import { createAuthSession } from "@/app/services/auth-storage";
import { http, parseApiError } from "@/app/services/api";
import type { AuthTokenResponse } from "@/app/types/api";
import { useLanguage } from "@/app/contexts/LanguageContext";
import { LANGUAGES, type Lang } from "@/app/i18n/types";
import { cn } from "@/app/utils/tw-merge";
import { RequiredMark } from "@/app/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu";

const REMEMBERED_EMAIL_KEY = "lumina_marketplace_remembered_email";
const SLIDE_IDS = ["catalog", "billing", "security"] as const;
const FLAGS: Record<Lang, string> = {
  vi: "🇻🇳",
  en: "🇬🇧",
  ja: "🇯🇵",
};

type SlideId = (typeof SLIDE_IDS)[number];

type Slide = {
  id: SlideId;
  product: string;
  tagline: string;
  description: string;
  features: string[];
};

export function LoginPage() {
  const { t } = useTranslation();
  const { isAuthenticated, setSession } = useAuth();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState(
    () => localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ""
  );
  const [password, setPassword] = React.useState("");
  const [rememberMe, setRememberMe] = React.useState(
    () => !!localStorage.getItem(REMEMBERED_EMAIL_KEY)
  );
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState("");

  const from = (location.state as { from?: string } | null)?.from;

  const slides = React.useMemo<Slide[]>(
    () =>
      SLIDE_IDS.map((id) => ({
        id,
        product: t(`auth.login.slides.${id}.product`),
        tagline: t(`auth.login.slides.${id}.tagline`),
        description: t(`auth.login.slides.${id}.description`),
        features: [
          t(`auth.login.slides.${id}.feature0`),
          t(`auth.login.slides.${id}.feature1`),
          t(`auth.login.slides.${id}.feature2`),
        ],
      })),
    [t]
  );

  const login = useMutation({
    mutationFn: async () =>
      http.post<AuthTokenResponse, { email: string; password: string }>(
        "/api/v1/auth/login",
        { email: email.trim(), password },
        { withAuth: false }
      ),
    onSuccess: (data) => {
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      setSession(createAuthSession(data));
      toast.success(t("toast.loginSuccess"));
      navigate(from ?? ROUTES.upload, { replace: true });
    },
    onError: (err) => {
      const message = parseApiError(err);
      setError(message);
      toast.error(message);
    },
  });

  if (isAuthenticated) {
    return <Navigate to={ROUTES.upload} replace />;
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) return;
    login.mutate();
  };

  return (
    <div className="flex min-h-screen overflow-hidden bg-[#fafafa]">
      <div className="relative flex w-full flex-col justify-center border-r border-[#f0f0f0] bg-white px-6 py-10 sm:px-10 lg:w-[40%] xl:px-16">
        <div className="pointer-events-none absolute top-0 right-0 h-64 w-64 rounded-full bg-[radial-gradient(circle_at_top_right,rgba(230,76,91,0.05)_0%,transparent_72%)]" />

        <div className="absolute top-5 right-5">
          <LangSwitchInline lang={lang} onSet={setLang} />
        </div>

        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 flex items-center gap-3">
            <img
              src="/lumina-logo.png"
              alt="Lumina"
              className="h-9 w-9 object-contain"
            />
            <span className="text-base font-bold text-[#e64c5b]">
              Lumina Marketplace
            </span>
          </div>

          <div className="mb-8">
            <h1 className="mb-1.5 text-2xl font-bold tracking-tight text-foreground">
              {t("auth.login.welcomeBack")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("auth.login.subtitle")}
            </p>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="login-email"
                className="mb-1.5 block text-xs font-semibold tracking-wide text-foreground uppercase"
              >
                {t("auth.login.emailLabel")}
                <RequiredMark />
              </label>
              <div className="group relative">
                <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-[#e64c5b]" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.login.emailPlaceholder")}
                  disabled={login.isPending}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pr-4 pl-10 text-sm outline-none transition-all focus:border-[#e64c5b] focus:bg-white focus:ring-2 focus:ring-[#e64c5b]/10"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="mb-1.5 block text-xs font-semibold tracking-wide text-foreground uppercase"
              >
                {t("auth.login.passwordLabel")}
                <RequiredMark />
              </label>
              <div className="group relative">
                <Lock className="absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-[#e64c5b]" />
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.login.passwordPlaceholder")}
                  disabled={login.isPending}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pr-10 pl-10 text-sm outline-none transition-all focus:border-[#e64c5b] focus:bg-white focus:ring-2 focus:ring-[#e64c5b]/10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-0.5">
              <label
                className="flex cursor-pointer items-center gap-2 select-none"
                onClick={() => setRememberMe((v) => !v)}
              >
                <div
                  className={cn(
                    "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-2 transition-all",
                    rememberMe
                      ? "border-[#e64c5b] bg-[#e64c5b]"
                      : "border-gray-300 bg-transparent"
                  )}
                >
                  {rememberMe && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {t("auth.login.rememberMe")}
                </span>
              </label>
              <span className="text-[11px] text-gray-400">
                {t("auth.login.internalOnly")}
              </span>
            </div>

            <button
              type="submit"
              disabled={login.isPending || !email.trim() || !password}
              className="mt-2 w-full cursor-pointer rounded-lg bg-[#e64c5b] py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(230,76,91,0.35)] transition-all hover:bg-[#c5001d] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {login.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("auth.login.loggingIn")}
                </span>
              ) : (
                t("auth.login.signInButton")
              )}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#f0f0f0]" />
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-[#ffd6dc] bg-[#fff5f6] p-4">
            <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#e64c5b]/10">
              <Sparkles className="h-3.5 w-3.5 text-[#e64c5b]" />
            </div>
            <div>
              <p className="mb-0.5 text-xs font-semibold text-foreground">
                {t("auth.login.exploreTitle")}
              </p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t("auth.login.exploreDesc")}
              </p>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-muted-foreground opacity-50">
            {t("auth.login.copyright")}
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block lg:w-[60%]">
        <SlidePanel slides={slides} />
      </div>
    </div>
  );
}

function SlidePanel({ slides }: { slides: Slide[] }) {
  const [current, setCurrent] = React.useState(0);

  const prev = React.useCallback(() => {
    setCurrent((value) => (value - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const next = React.useCallback(() => {
    setCurrent((value) => (value + 1) % slides.length);
  }, [slides.length]);

  React.useEffect(() => {
    const timer = window.setInterval(next, 6500);
    return () => window.clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <div className="relative flex h-full items-center justify-center overflow-hidden bg-[#f5f6fa] p-8 xl:p-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(230,76,91,0.12),transparent_24%),radial-gradient(circle_at_80%_18%,rgba(99,102,241,0.10),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)]" />
      <div className="relative flex h-full w-full max-w-4xl flex-col justify-center rounded-[28px] border border-white/70 bg-white/78 p-8 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur xl:p-10">
        <div className="flex flex-col gap-8">
          <SlideCanvas slide={slide} />
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#e64c5b]">
              {slide.product}
            </p>
            <h2 className="mb-2 max-w-2xl text-2xl font-bold tracking-tight text-gray-900 xl:text-[1.65rem]">
              {slide.tagline}
            </h2>
            <p className="mb-4 max-w-2xl text-sm leading-relaxed text-gray-500">
              {slide.description}
            </p>
            <div className="mb-5 flex flex-col gap-1.5">
              {slide.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border border-[#e64c5b]/20 bg-[#e64c5b]/10">
                    <Check className="h-2.5 w-2.5 text-[#e64c5b]" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between border-t border-black/[0.06] pt-4">
              <div className="flex items-center gap-1.5">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrent(index)}
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-300",
                      index === current
                        ? "w-5 bg-[#e64c5b]"
                        : "w-1.5 bg-black/[0.14]"
                    )}
                  />
                ))}
              </div>

              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={prev}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-black/[0.08] bg-black/[0.04] transition-all hover:bg-black/[0.09]"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-500" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e64c5b] shadow-[0_2px_8px_rgba(230,76,91,0.3)] transition-all hover:bg-[#c5001d]"
                >
                  <ChevronRight className="h-4 w-4 text-white" />
                </button>
              </div>

              <span className="font-mono text-xs tabular-nums text-gray-300">
                {String(current + 1).padStart(2, "0")} /{" "}
                {String(slides.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LangSwitchInline({
  lang,
  onSet,
}: {
  lang: Lang;
  onSet: (l: Lang) => void;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          title={t("lang.switch.title")}
          aria-label={t("lang.switch.title")}
        >
          <Globe className="h-3.5 w-3.5 opacity-70" />
          <span aria-hidden className="text-sm leading-none">{FLAGS[lang]}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("lang.switch.title")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((lg) => {
          const active = lg === lang;
          return (
            <DropdownMenuItem
              key={lg}
              onClick={() => onSet(lg)}
              className="cursor-pointer"
            >
              <span aria-hidden className="text-base leading-none">{FLAGS[lg]}</span>
              <span className="flex-1">{t(`lang.switch.${lg}` as "lang.switch.vi")}</span>
              {active && <Check className="h-3.5 w-3.5 opacity-80" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SlideCanvas({ slide }: { slide: Slide }) {
  const icon =
    slide.id === "catalog" ? (
      <Package2 className="h-5 w-5 text-[#e64c5b]" />
    ) : slide.id === "billing" ? (
      <CreditCard className="h-5 w-5 text-[#e64c5b]" />
    ) : (
      <ShieldCheck className="h-5 w-5 text-[#e64c5b]" />
    );

  return (
    <div className="relative rounded-[24px] border border-[#eef0f4] bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-6 shadow-[0_20px_40px_rgba(15,23,42,0.06)]">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff1f3]">
            {icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{slide.product}</p>
            <p className="text-xs text-gray-400">Lumina operations surface</p>
          </div>
        </div>
        <span className="rounded-full border border-[#ffd8de] bg-[#fff5f6] px-2.5 py-1 text-[11px] font-semibold text-[#e64c5b]">
          Live
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <CanvasCard
          title={slide.id === "catalog" ? "Releases" : slide.id === "billing" ? "Requests" : "Sessions"}
          value={slide.id === "catalog" ? "12" : slide.id === "billing" ? "28" : "96"}
          tone="red"
        />
        <CanvasCard
          title={slide.id === "catalog" ? "Plans linked" : slide.id === "billing" ? "Pending confirm" : "Role checks"}
          value={slide.id === "catalog" ? "4" : slide.id === "billing" ? "7" : "100%"}
          tone="violet"
        />
      </div>

      <div className="mt-4 rounded-2xl border border-[#edf0f5] bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">
            Workflow
          </p>
          <span className="text-xs text-gray-400">Marketplace</span>
        </div>
        <div className="space-y-2.5">
          {slide.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-center justify-between rounded-xl border border-[#f2f4f7] bg-[#fafbfc] px-3 py-2.5"
            >
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#e64c5b]" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
              <div className="h-2 w-14 rounded-full bg-[linear-gradient(90deg,#f3d5da_0%,#fdecef_100%)]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CanvasCard({
  title,
  value,
  tone,
}: {
  title: string;
  value: string;
  tone: "red" | "violet";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-4",
        tone === "red"
          ? "border-[#ffd8de] bg-[#fff5f6]"
          : "border-[#e8e7ff] bg-[#f6f5ff]"
      )}
    >
      <p className="text-xs font-medium text-gray-500">{title}</p>
      <div className="mt-2 flex items-end justify-between">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <div
          className={cn(
            "h-8 w-16 rounded-xl",
            tone === "red"
              ? "bg-[linear-gradient(180deg,rgba(230,76,91,0.18)_0%,rgba(230,76,91,0.04)_100%)]"
              : "bg-[linear-gradient(180deg,rgba(129,140,248,0.18)_0%,rgba(129,140,248,0.04)_100%)]"
          )}
        />
      </div>
    </div>
  );
}
